import Edit from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import type { FiltersContext } from '@openx/components-filters/lib/context/types.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { criteriaHasSelectedFilters } from '../../utils/criteriaHasSelectedFilters.ts';
import type { DealPromptContextType } from '../../utils/types.ts';

type ActionButtonsProps = {
  isPreview: boolean;
  loading: boolean;
  disabled: boolean;
  value: string;
  buttonLabel: string;
  onCancel: () => void;
  onPrimaryClick: () => void;
  onEditPrompt: () => void;
  criteria: FiltersContext['criteria'];
  contextType: DealPromptContextType;
};

export const ActionButtons = ({
  isPreview,
  loading,
  disabled,
  value,
  buttonLabel,
  onCancel,
  onPrimaryClick,
  onEditPrompt,
  criteria,
  contextType,
}: ActionButtonsProps) => {
  const { t } = useTranslation();
  const hasPrompt = !!value.trim();

  const hasAnyFilter = useMemo(() => criteriaHasSelectedFilters(criteria.filters), [criteria?.filters]);

  const isSearchDisabled = disabled || loading || (!hasPrompt && !hasAnyFilter);

  return (
    <Stack direction="row" spacing={1} sx={{ ml: -2, justifyContent: 'flex-end' }}>
      {loading && (
        <Button onClick={onCancel} variant="text" data-test="deal-prompt-cancel">
          {t('Cancel')}
        </Button>
      )}

      {isPreview ? (
        <IconButton
          data-test="deal-prompt-edit"
          data-pendo-feature={`deal-prompt-main-${contextType}-edit-prompt`}
          onClick={onEditPrompt}
          disabled={!isPreview && isSearchDisabled}
        >
          <Edit />
        </IconButton>
      ) : (
        <Button
          data-test="deal-prompt-search"
          data-pendo-feature={`deal-prompt-main-${contextType}-search`}
          onClick={onPrimaryClick}
          disabled={isSearchDisabled}
          variant="contained"
          color="primary"
          sx={{
            minWidth: 'fit-content',
            alignSelf: 'flex-end',
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : buttonLabel}
        </Button>
      )}
    </Stack>
  );
};
